import { persistStateToWebStorage } from '@rcm/redux-saga-utils';
import { isAnyOf } from '@reduxjs/toolkit';
import { call, spawn, StrictEffect, takeEvery } from 'redux-saga/effects';
import { submitShipping, unsubmitShipping } from '../actions';
import api from '../api';
import { SHIPPING_IS_SUBMITTED_STORAGE_KEY, SHIPPING_STORAGE_KEY } from '../constants';
import * as shipping from '../slices/shipping';
import * as view from '../slices/view';
const { findCurrentCountryRule, findGeoCodes } = api.endpoints;

const storage = window.localStorage;


// From the @rcm/redux-global README: "Every `onInitSaga` must have an explicit
// return type that does not cause any circular references. For example, the
// return type may not reference the module's `RootState` explicitly or
// implicitly. The easiest way to do this is to set the return type to
// `Generator<StrictEffect>`."

const initPersistShipping = function* (): Generator<StrictEffect> {
    // This is an init generator. Init generators must spawn all independent
    // threads rather than fork them.

    yield spawn(persistStateToWebStorage, {
        action: isAnyOf(
            shipping.setShippingCity,
            shipping.setShippingCountry,
            shipping.setShippingGeoCode,
            shipping.setShippingPostalCode,
            shipping.setShippingState,
            api.endpoints.findCurrentCountryRule.matchFulfilled
        ),
        selector: shipping.selectShipping,
        storage,
        storageKey: SHIPPING_STORAGE_KEY,
        throttle: 1000,
    });

    yield spawn(persistStateToWebStorage, {
        action: isAnyOf(
            submitShipping,
            unsubmitShipping
        ),
        selector: view.selectViewIsShippingSubmitted,
        storage,
        storageKey: SHIPPING_IS_SUBMITTED_STORAGE_KEY,
        throttle: 1000,
    });

    yield spawn(handleShippingErrors);
};

// This is used to prevent a potential issue where the user may get stuck on an
// error screen from which there is no escape, even after reloading the page.
// This could be triggered as a result of giving a bad address, or if an address
// was automatically selected by the system. If the error occurs, we clear out
// the current persisted shipping address so that it cannot cause the error to
// occur again after the page reloads.
function* handleShippingErrors(): Generator<StrictEffect> {
    yield takeEvery(
        isAnyOf(
            findCurrentCountryRule.matchRejected,
            findGeoCodes.matchRejected
        ),
        function* (action) {
            if (!action.payload || (findGeoCodes.matchRejected(action) && action.payload.status == 404)) {
                return;
            }
            yield call([storage, storage.removeItem], SHIPPING_STORAGE_KEY);
            yield call([storage, storage.removeItem], SHIPPING_IS_SUBMITTED_STORAGE_KEY);
        }
    );
}

export default initPersistShipping;
